import { ComponentProps, PropsWithChildren } from "react";
import { useLocation } from "../utils/location";
import { defineMessages, useIntl } from "react-intl";

const DEFAULT_META_IMAGE_URL =
  "https://aqora-static.s3.fr-par.scw.cloud/featured-platform.png" as const;

const DEFAULT_METATAGS = defineMessages({
  title: {
    id: "defaultMetatagsTitle",
    defaultMessage: "Aqora",
  },
  description: {
    id: "defaultMetatagsDescription",
    defaultMessage:
      "Join Aqora for hands-on quantum computing hackathons and online competitions. Learn, compete, and connect with a global community of experts.",
  },
  imageAlt: {
    id: "defaultMetatagsImageAlt",
    defaultMessage: "Aqora banner",
  },
});

export type MetaProps = ComponentProps<"meta">;

export interface MetaLayoutProps extends PropsWithChildren {
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaImageUrl?: string | null;
  metaImageAlt?: string | null;
  hideTitleTemplate?: boolean;
}

export function MetaLayout({
  metaTitle,
  metaDescription,
  metaImageAlt,
  metaImageUrl,
  children,
}: Readonly<MetaLayoutProps>) {
  const intl = useIntl();
  const { href } = useLocation();

  const metatags = {
    title: metaTitle ?? intl.formatMessage(DEFAULT_METATAGS.title),
    description:
      metaDescription ?? intl.formatMessage(DEFAULT_METATAGS.description),
    image: metaImageUrl ?? DEFAULT_META_IMAGE_URL,
    imageAlt: metaImageAlt ?? intl.formatMessage(DEFAULT_METATAGS.imageAlt),
  };

  return (
    <>
      <link rel="canonical" href={href} />
      <meta property="og:url" content={href} />
      <meta name="twitter:title" content={metatags.title} />
      <meta property="og:title" content={metatags.title} />
      <title>{metatags.title}</title>
      <meta name="description" content={metatags.description} />
      <meta property="og:description" content={metatags.description} />
      <meta name="twitter:description" content={metatags.description} />
      <meta name="og:image" content={metatags.image} />
      <meta property="og:image" content={metatags.image} />
      <meta name="twitter:image" content={metatags.image} />
      <meta property="og:image:alt" content={metatags.imageAlt} />
      <meta name="twitter:image:alt" content={metatags.imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      {children}
    </>
  );
}
